import React, { useState, useEffect } from "react";
import { DollarSign, Calculator, TrendingUp, ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

// Free, no-key exchange rate API
const API_URL = "https://open.er-api.com/v6/latest/USD";

export function useUsdToMxn() {
  const [rate, setRate] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    let active = true;
    const fetchRate = async () => {
      try {
        const res = await fetch(API_URL);
        const data = await res.json();
        if (active && data && data.rates && data.rates.MXN) {
          setRate(data.rates.MXN);
          setUpdatedAt(new Date(data.time_last_update_unix * 1000));
        }
      } catch (e) {
        // silent — UI shows fallback state
      }
    };
    fetchRate();
    return () => { active = false; };
  }, []);

  return { rate, updatedAt };
}

export default function CurrencyConverter() {
  const { rate, updatedAt } = useUsdToMxn();
  const [usd, setUsd] = useState("");
  const [mxn, setMxn] = useState("");

  const convert = (value) => {
    setUsd(value);
    if (!rate) return;
    const num = parseFloat(value);
    if (isNaN(num)) { setMxn(""); return; }
    setMxn((num * rate).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="glass-panel rounded-2xl p-6 sm:p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-ochre/10 flex items-center justify-center border border-ochre/30">
          <Calculator className="w-5 h-5 text-ochre" />
        </div>
        <div>
          <span className="block text-white font-heading font-bold text-sm tracking-wider uppercase">Conversor USD → MXN</span>
          <span className="text-white/40 text-xs tracking-wide">Cotizaciones de servicios aéreos</span>
        </div>
      </div>

      {/* Live Rate */}
      <div className="flex items-center justify-between glass-panel rounded-lg px-4 py-3 mb-6">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-ochre" />
          <span className="text-cirrus text-xs tracking-wider">1 USD =</span>
        </div>
        <div className="text-right">
          {rate ? (
            <>
              <span className="text-ochre font-mono font-bold text-lg tracking-wider">{rate.toFixed(2)} MXN</span>
              <span className="block text-white/30 text-[10px] tracking-wider">
                {updatedAt ? `Actualizado: ${updatedAt.toLocaleDateString("es-MX")}` : "Cargando..."}
              </span>
            </>
          ) : (
            <span className="text-white/40 text-xs">Obteniendo tipo de cambio...</span>
          )}
        </div>
      </div>

      {/* Calculator */}
      <div className="space-y-4">
        <div>
          <label className="block text-white/50 text-xs font-heading tracking-wider uppercase mb-2 flex items-center gap-1">
            <DollarSign className="w-3 h-3 text-ochre" /> Dólares (USD)
          </label>
          <input
            type="number"
            inputMode="decimal"
            value={usd}
            onChange={(e) => convert(e.target.value)}
            placeholder="0.00"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white font-mono text-xl tracking-wider focus:outline-none focus:border-ochre/50 transition-colors"
          />
        </div>

        <div className="flex justify-center">
          <ArrowRight className="w-5 h-5 text-ochre/50 rotate-90" />
        </div>

        <div>
          <label className="block text-white/50 text-xs font-heading tracking-wider uppercase mb-2">
            Pesos Mexicanos (MXN)
          </label>
          <div className="w-full bg-ochre/5 border border-ochre/20 rounded-lg px-4 py-3 text-ochre font-mono text-xl tracking-wider">
            {mxn || "0.00"}
          </div>
        </div>

        <p className="text-white/30 text-xs tracking-wide pt-2">
          Tipo de cambio referencial. La cotización final se confirma al momento de la reserva.
        </p>
      </div>
    </motion.div>
  );
}